import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ContactCenterApiService } from '../../../v2/apis/contact-center/contact-center-api.service';
import { PlatformRegionService } from '../../../service/shared/platform-region.service';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, filter, takeUntil } from 'rxjs/operators';

export interface RequestData {
  type?: string;
  description?: string;
  attachments?: File[];
}

@Component({
    selector: 'app-create-bug',
    templateUrl: './create-bug.component.html',
    styleUrl: './create-bug.component.scss',
    standalone: false
})
export class CreateBugComponent implements OnInit, OnDestroy {
  requestForm: FormGroup;
  selectedFiles: File[] = [];
  selectedFilesDisplay: Array<{ file: File, name: string }> = [];
  requestTypes = [
    { value: 'bugs', label: 'Bug' },
    { value: 'enhancements', label: 'Enhancement' },
    { value: 'general_questions', label: 'General Questions' }
  ];
  isSubmitting: boolean = false;
  isFileUplaodEnabled: boolean = true;
  filteredUsers: any[] = [];
  noResultsFound: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CreateBugComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RequestData,
    private toastr: ToastrService,
    private contactCenterService: ContactCenterApiService,
    private platformRegionService: PlatformRegionService
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.setupRequestorTypeahead();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeForm(): void {
    const loggedInUserName = sessionStorage.getItem('fullName') || '';
    this.requestForm = this.fb.group({
      type: [this.data?.type || '', Validators.required],
      requestor: [loggedInUserName, [Validators.required, this.validRequestorValidator.bind(this)]],
      description: [this.data?.description || '', [Validators.required, Validators.minLength(10)]]
    });
    this.requestForm.get('type')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((type: string) => {
      this.updateDescriptionValidators(type);
    });
    this.updateDescriptionValidators(this.requestForm.get('type')?.value);
  }

  updateDescriptionValidators(type: string): void {
    const descriptionControl = this.requestForm.get('description');
    if (!descriptionControl) {
      return;
    }

    const validators = [Validators.required, Validators.minLength(10)];
    if (type?.toLowerCase() === 'enhancements') {
      validators.push(Validators.maxLength(300));
    }
    descriptionControl.setValidators(validators);
    descriptionControl.updateValueAndValidity({ emitEvent: false });
  }

  validRequestorValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;
    // If it's an object (selected from dropdown), it's valid
    if (typeof value === 'object' && value.wwid) return null;
    // If it matches the logged-in user name, it's valid
    const loggedInUserName = sessionStorage.getItem('fullName') || '';
    if (value === loggedInUserName) return null;
    // Otherwise it's an invalid free-text entry
    return { invalidRequestor: true };
  }

  setupRequestorTypeahead(): void {
    this.requestForm.get('requestor')?.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged(),
      filter(value => typeof value === 'string' && value.length >= 3),
      switchMap(searchTerm => {
        const wwid = Number(sessionStorage.getItem('userId'));
        const businessUnitCode = sessionStorage.getItem('businessUnitCode') || '';
        const region = this.platformRegionService.selectedRegion?.name || '';
        return this.contactCenterService.getRequestorUsers(wwid, businessUnitCode, region, searchTerm, 10, 0);
      })
    ).subscribe({
      next: (response) => {
        this.filteredUsers = Array.isArray(response) ? response : (response?.data || response?.users || []);
        this.noResultsFound = this.filteredUsers.length === 0;
      },
      error: () => {
        this.filteredUsers = [];
        this.noResultsFound = true;
      }
    });
  }

  displayUser(user: any): string {
    if (!user) return '';
    if (typeof user === 'string') return user;
    return user.display_name || '';
  }

  preventSpecialChars(event: KeyboardEvent): void {
    const pattern = /^[a-zA-Z0-9\s]$/;
    if (!pattern.test(event.key)) {
      event.preventDefault();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const maxFiles = 5;
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (input.files && input.files.length > 0) {
      const newFiles = Array.from(input.files);
      const validFiles = newFiles.filter(file => file.size <= maxSize);
      const invalidFiles = newFiles.filter(file => file.size > maxSize);
      if (invalidFiles.length > 0) {
        this.toastr.error('Some files exceed the 5MB size limit and were not added.', 'File Size Exceeded');
      }
      if (this.selectedFiles.length + validFiles.length > maxFiles) {
        this.toastr.warning('You can upload a maximum of 5 files.', 'File Limit Exceeded');
        return;
      }
      validFiles.forEach(file => {
        this.selectedFiles.push(file);
        this.selectedFilesDisplay.push({
          file: file,
          name: file.name
        });
      });
    }
  }

  clearSelectedFileInput(): void {
    this.selectedFilesDisplay = [];
    this.selectedFiles = [];
  }

  removeFile(index: number): void {
    this.selectedFilesDisplay.splice(index, 1);
    this.selectedFiles.splice(index, 1);
  }

  onSubmit(): void {
    if (this.requestForm.valid) {
      this.isSubmitting = true;
      const file = this.selectedFiles.length > 0 ? this.selectedFiles[0] : null;
      const requestorValue = this.requestForm.value.requestor;
      const requestedByWwid = typeof requestorValue === 'object' ? requestorValue?.wwid : Number(sessionStorage.getItem('userId'));
      const payload = {
        request_type: (this.requestForm.value.type === 'bugs' ? 'Bugs' : this.requestForm.value.type === 'enhancements' ? 'Enhancements' : 'general questions') as 'Bugs' | 'Enhancements' | 'general questions',
        user_request: this.requestForm.value.description,
        description: this.requestForm.value.description,
        given_by: Number(sessionStorage.getItem('userId')),
        requested_by: requestedByWwid,
        business_unit_code: sessionStorage.getItem('businessUnitCode') || '',
        region: this.platformRegionService.selectedRegion?.name || '',
        //attachment: this.selectedFiles.length > 0 ? this.selectedFiles[0].name : ''
      };

      this.contactCenterService.createRequest(file, payload).subscribe({
        next: (response) => {
          this.toastr.success('Request submitted successfully!', 'Success');
          this.dialogRef.close({ response, type: payload.request_type });
          this.isSubmitting = false;
        },
        error: (error) => {
          console.error('Error creating request:', error);
          const errorMessage = error?.error?.message || error?.message || 'Failed to submit request. Please try again.';
          this.toastr.error(errorMessage, 'Error');
          this.isSubmitting = false;
        }
      });
    } else {
      Object.keys(this.requestForm.controls).forEach(key => {
        this.requestForm.get(key)?.markAsTouched();
      });
      this.toastr.warning('Please fill in all required fields', 'Validation Error');
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
